// owner/screens/Unit.js — one unit (/properties/:id): photo, name, code, status, the facts an owner reads today
// (city, type, contract start, where it is listed) and three tabs — calendar, bookings, reviews. Built in the
// approved kit (no mock-up of its own; reviewed on dev). Not carried over from the old shared page: the "joined"
// date and the dead-end «تعديل العقار» button (Adil, 4 Nov 2026).

import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import { joinList } from '../../utils/localeText';
import Icon from '../ui/Icon';
import PlatformMark from '../ui/PlatformMark';
import { SkeletonBlocks, ErrorState, EmptyState } from '../ui/States';
import { UnitThumb, UnitStatusChip, useUnitTypeLine } from '../ui/UnitBits';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerUnit, useUnitStatuses } from '../api/ownerReads';
import UnitCalendar from './unit/UnitCalendar';
import UnitBookings from './unit/UnitBookings';
import UnitReviews from './unit/UnitReviews';

const TABS = ['calendar', 'bookings', 'reviews'];
const LINK_PLATFORMS = ['airbnb', 'booking', 'gathern'];  // Property.publicLinks keys

const Unit = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const f = useOwnerFormat();
  const typeLine = useUnitTypeLine();
  const unit = useOwnerUnit(id);
  const join = (parts) => joinList(parts, language);
  const statuses = useUnitStatuses(id ? [id] : []);
  const [tab, setTab] = useState('calendar');

  if (unit.isLoading) return <SkeletonBlocks blocks={3} />;
  if (unit.isError) {
    const status = unit.error?.response?.status;
    if (status === 403 || status === 404) {
      return (
        <EmptyState icon="units" title={t('ownerApp.unit.notYours')}
          action={<Link className="o-btn o-btn-primary" to="/properties">{t('ownerApp.unit.backToUnits')}</Link>} />
      );
    }
    return <ErrorState title={t('ownerApp.unit.loadError')} onRetry={unit.refetch} />;
  }

  const u = unit.data;
  const links = LINK_PLATFORMS.filter((key) => u.publicLinks?.[key]);
  const type = typeLine(u);

  return (
    <>
      <Link to="/properties" className="o-back" aria-label={t('ownerApp.common.back')}><Icon name="back" /></Link>

      <div className="o-detail-hero">
        <UnitThumb photo={u.bookingPageSettings?.primaryPhoto} large />
        <div className="o-body">
          <h1>{u.name}</h1>
          {u.propertyCode && <span className="o-code o-ltr">{u.propertyCode}</span>}
        </div>
        <UnitStatusChip status={statuses.byId[id]?.status} />
      </div>

      <section className="o-section">
        <ul className="o-sheet o-kv">
          {(u.city || u.address) && (
            <li><span className="o-k"><Icon name="pin" size="sm" />{t('ownerApp.unit.city')}</span>
              <span className="o-v">{join([u.city, u.address])}</span></li>
          )}
          {type.length > 0 && (
            <li><span className="o-k"><Icon name="bed" size="sm" />{t('ownerApp.unit.type')}</span><span className="o-v">{join(type)}</span></li>
          )}
          {u.contractStartDate && (
            <li><span className="o-k"><Icon name="calendar" size="sm" />{t('ownerApp.unit.contractStart')}</span>
              <span className="o-v">{f.date(u.contractStartDate)}</span></li>
          )}
          {links.length > 0 && (
            <li className="o-kv-stack"><span className="o-k"><Icon name="globe" size="sm" />{t('ownerApp.unit.listedOn')}</span>
              <span className="o-links">
                {links.map((key) => (
                  <a key={key} href={u.publicLinks[key]} target="_blank" rel="noopener noreferrer"><PlatformMark platform={key} withName /></a>
                ))}
              </span></li>
          )}
        </ul>
      </section>

      <div className="o-seg" role="tablist" aria-label={u.name}>
        {TABS.map((key) => (
          <button key={key} type="button" role="tab" id={`o-tab-${key}`} aria-controls={`o-panel-${key}`}
            aria-selected={tab === key} onClick={() => setTab(key)}>
            {t(`ownerApp.unit.tabs.${key}`)}
          </button>
        ))}
      </div>

      <section className="o-section" role="tabpanel" id={`o-panel-${tab}`} aria-labelledby={`o-tab-${tab}`}>
        {/* keyed by unit: a tab never shows the previous unit's data while this one loads */}
        {tab === 'calendar' && <UnitCalendar key={id} unitId={id} />}
        {tab === 'bookings' && <UnitBookings key={id} unitId={id} />}
        {tab === 'reviews' && <UnitReviews key={id} unitId={id} />}
      </section>
    </>
  );
};

export default Unit;
